import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { EntityType } from "./useEntityImages";

// ── Types ────────────────────────────────────────────────────────────────

export interface EntityFile {
  id: string;
  entity_id: string;
  org_id: string;
  storage_path: string;
  public_url: string;
  filename: string;
  file_size: number;
  mime_type: string;
  created_at: string;
  service_log_id?: string;
}

const TABLE: Record<EntityType, string> = {
  asset: "asset_file",
  part: "part_file",
};

const ID_COL: Record<EntityType, string> = {
  asset: "asset_id",
  part: "part_id",
};

const BUCKET = "asset-files";

// ── Helpers ──────────────────────────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileIcon(mimeType: string): string {
  if (mimeType === "application/pdf") return "📄";
  if (mimeType.startsWith("image/")) return "🖼️";
  if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
  if (mimeType.includes("sheet") || mimeType.includes("excel")) return "📊";
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "📋";
  return "📎";
}

// ── Query ────────────────────────────────────────────────────────────────

export function useEntityFiles(entityType: EntityType, entityId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["entity-files", entityType, entityId],
    queryFn: async () => {
      let query = supabase
        .from(TABLE[entityType] as any)
        .select("*")
        .eq(ID_COL[entityType], entityId!);

      // Exclude service-log-scoped files from the general Files & Manuals section
      if (entityType === "asset") {
        query = (query as any).is("service_log_id", null);
      }

      const { data, error } = await (query as any).order("created_at", { ascending: true });

      if (error) throw error;

      return ((data || []) as any[]).map((row) => ({
        ...row,
        entity_id: row[ID_COL[entityType]],
      })) as EntityFile[];
    },
    enabled: !!entityId && isAuthenticated && !authLoading,
  });
}

// Returns all service-log-scoped files for an asset, grouped by service_log_id
export function useServiceLogFiles(assetId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["service-log-files", assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_file" as any)
        .select("*")
        .eq("asset_id", assetId!)
        .not("service_log_id", "is", null)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const map: Record<string, EntityFile[]> = {};
      for (const row of (data || []) as any[]) {
        const key = row.service_log_id as string;
        if (!map[key]) map[key] = [];
        map[key].push({ ...row, entity_id: row.asset_id });
      }
      return map;
    },
    enabled: !!assetId && isAuthenticated && !authLoading,
  });
}

// ── Upload ───────────────────────────────────────────────────────────────

export function useUploadEntityFile(entityType: EntityType) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      entityId,
      orgId,
      serviceLogId,
    }: {
      file: File;
      entityId: string;
      orgId: string;
      serviceLogId?: string;
    }) => {
      const storagePath = `${orgId}/${entityType}/${entityId}/${Date.now()}_${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, { cacheControl: "3600", upsert: false });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(storagePath);

      const insertPayload: Record<string, unknown> = {
        [ID_COL[entityType]]: entityId,
        org_id: orgId,
        storage_path: storagePath,
        public_url: urlData.publicUrl,
        filename: file.name,
        file_size: file.size,
        mime_type: file.type || "application/octet-stream",
      };
      if (serviceLogId) insertPayload.service_log_id = serviceLogId;

      const { data, error: insertError } = await supabase
        .from(TABLE[entityType] as any)
        .insert([insertPayload])
        .select()
        .single();

      if (insertError) throw insertError;

      return { ...(data as any), entity_id: (data as any)[ID_COL[entityType]] } as EntityFile;
    },
    onSuccess: (_data, { entityId, serviceLogId }) => {
      if (serviceLogId) {
        queryClient.invalidateQueries({ queryKey: ["service-log-files", entityId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["entity-files", entityType, entityId] });
      }
      toast({ title: "File uploaded" });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });
}

// ── Delete ───────────────────────────────────────────────────────────────

export function useDeleteEntityFile(entityType: EntityType) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      entityId,
    }: {
      file: EntityFile;
      entityId: string;
    }) => {
      await supabase.storage.from(BUCKET).remove([file.storage_path]);

      const { error } = await supabase
        .from(TABLE[entityType] as any)
        .delete()
        .eq("id", file.id);

      if (error) throw error;
    },
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ["entity-files", entityType, entityId] });
      toast({ title: "File deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error deleting file", description: error.message, variant: "destructive" });
    },
  });
}
